import { useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, LinearProgress, Paper, Typography } from "@mui/material";
import { Add, Delete, Edit, FilterList, Visibility } from "@mui/icons-material";

import { DialogBox, Drawer, DrawerControl, MetaDrivenTable, PaginationControls, SearchField, StatusChip } from "../../../shared/components";
import { useGetMaterialReq } from "../hooks/useGetMaterialReq";
import { AddRequest, DeleteConfirmation, RequestDetails, RequestDetailsHeader } from "../components";
import { useDeleteRequest } from "../hooks/useDeleteRequest";

export const MaterialRequests = () => {

    const { projectId } = useParams();

    const [newRequest, setNewRequest] = useState(false);

    const [details, setDetails] = useState<{
        status: boolean,
        requestId: string | null
    }>({
        status: false,
        requestId: null
    });

    const [editDetails, setEditDetails] = useState<{
        status: boolean,
        requestId: string | null
    }>({
        status: false,
        requestId: null
    });

    const [deleteDetails, setDeleteDetails] = useState<{
        status: boolean,
        requestId: string | null
    }>({
        status: false,
        requestId: null
    });

    const {
        data, isFetching, isPending, isError, error, searchDraft, pagination,
        refetch, changePage, changeRowsPerPage, handleChangeSearch, handleClear, handleSearch
    } = useGetMaterialReq({ projectId: projectId });

    const {
        deleting,
        deleteError,
        handleDelete
    } = useDeleteRequest({
        onSuccess: () => {
            setDeleteDetails({ status: false, requestId: null });
            refetch();
        }
    });

    const headers = [
        { "alias_name": "material_ref", "label": "Material REF", "display_order": 1, is_show: true },
        { "alias_name": "type_display_text", "label": "Type", "display_order": 2, is_show: true },
        { "alias_name": "description", "label": "Description", "display_order": 3, is_show: true },
        { "alias_name": "order_placed_date", "label": "Order Placed Date", "display_order": 4, is_show: true },
        { "alias_name": "est_delivery_date", "label": "Estimated Delivery Date", "display_order": 5, is_show: true },
        { "alias_name": "point_of_contact_info", "label": "Point of Contact Info", "display_order": 6, is_show: true },
        { "alias_name": "status_display_text", "label": "Status", "display_order": 7, is_show: true },
        { "alias_name": "supplier_display_text", "label": "Supplier", "display_order": 8, is_show: true },
        { "alias_name": "required_date", "label": "Required Date", "display_order": 9, is_show: true },
        { "alias_name": "last_updated_comments", "label": "Last Updated Comments", "display_order": 10, is_show: true },
        { "alias_name": "project_title", "label": "Project Title", "display_order": 11, is_show: true },
        { "alias_name": "phase_name", "label": "Phase Name", "display_order": 12, is_show: true },
        { "alias_name": "activity_name", "label": "Activity Name", "display_order": 13, is_show: true },
        { "alias_name": "owner_name", "label": "Owner Name", "display_order": 14, is_show: true },
    ];

    const metrics = {
        activity_name: {
            type: 'component',
            element: ((row: { activity: string; activity_id: string; }) => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'start'}>
                    <Typography variant="body2">{row.activity}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.activity_id}</Typography>
                </Box>
            ))
        },
        status_display_text: {
            type: 'component',
            element: ((row: { status_display_text: string; }) => (
                <StatusChip label={row.status_display_text} color={
                    row.status_display_text === 'Requested' ? 'default' :
                        row.status_display_text === 'Approved' ? 'blue' :
                            row.status_display_text === 'Ordered' ? 'amber' :
                                row.status_display_text === 'Delivered' ? 'green' :
                                    row.status_display_text === 'Used' ? 'violet' :
                                        'default'
                } />
            ))
        },
        order_placed_date: {
            type: 'date',
        },
        est_delivery_date: {
            type: 'date',
        },
        required_date: {
            type: 'date',
        }
    };

    if (isPending || isFetching) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (isError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                <Alert
                    severity="warning"
                    sx={{
                        width: "auto",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    {error?.message || "Something went wrong!"}
                </Alert>
            </Box>
        );
    };

    return (
        <Box px={2}>

            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                <SearchField searchTerm={searchDraft} handleSearch={handleChangeSearch} handleClearSearch={handleClear} handleApplySearch={handleSearch} />
                <Box display={'flex'} alignItems={'center'} py={2} gap={2}>
                    <Button variant="outlined" color="primary" startIcon={<FilterList />}>Filters</Button>
                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => setNewRequest(true)}>New Material Request</Button>
                </Box>
            </Box>

            <Paper elevation={1} data-background-elevation={1}>
                <MetaDrivenTable headers={headers} data={data} dataMetrics={metrics} sortDisabled
                    actions={[
                        {
                            label: <Edit sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string }) => {
                                setEditDetails({
                                    status: true,
                                    requestId: row.id,
                                });
                            }
                        },
                        {
                            label: <Delete sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string }) => {
                                setDeleteDetails({
                                    status: true,
                                    requestId: row.id,
                                });
                            }
                        },
                        {
                            label: <Visibility sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string }) => {
                                setDetails({
                                    status: true,
                                    requestId: row.id,
                                });
                            }
                        }
                    ]}
                />
            </Paper>

            <PaginationControls
                total={pagination?.total}
                rowsPerPage={pagination?.limit}
                currentPage={pagination?.page}
                handlePageChange={changePage}
                handleRowsPerPage={changeRowsPerPage}
            />

            <DrawerControl
                title={editDetails.status ? "Edit Material Request" : "New Material Request"}
                subTitle={""}
                isOpen={newRequest || editDetails.status}
                handleClose={() => {
                    setNewRequest(false);
                    setEditDetails({ status: false, requestId: null })
                }}
                children={
                    <AddRequest
                        projectId={projectId}
                        refetch={refetch}
                        requestId={editDetails.requestId}
                        isEdit={editDetails.status}
                        onSuccess={function (): void {
                            setNewRequest(false);
                            setEditDetails({ status: false, requestId: null });
                        }}
                        onClose={() => {
                            setNewRequest(false);
                            setEditDetails({ status: false, requestId: null })
                        }}
                    />
                }
            />

            <Drawer
                isOpen={details.status}
                header={
                    <RequestDetailsHeader
                        onClose={() => { setDetails({ status: false, requestId: null }) }}
                        projectId={projectId}
                        requestId={details.requestId}
                    />
                }
                children={
                    <RequestDetails
                        projectId={projectId}
                        requestId={details.requestId}
                    />
                }
            />

            <DialogBox
                showModal={deleteDetails.status}
                title={"Delete Material Request"}
                subTitle={""}
                onModalClose={() => {
                    setDeleteDetails({ status: false, requestId: null })
                }}
                children={
                    <DeleteConfirmation
                        deleting={deleting}
                        onConfirm={() => {
                            handleDelete({ requestId: deleteDetails.requestId });
                        }}
                        onCancel={() => {
                            setDeleteDetails({ status: false, requestId: null });
                        }}
                    />
                }
            />

        </Box>
    )
}